import { create } from 'zustand'
import type { TaskGraph } from '@/types/task'

/**
 * Build phase state machine — single source of truth for build lifecycle.
 *
 * Transitions:
 *   IDLE → CONFIRMING → BUILDING → COMPLETED → ITERATING → BUILDING
 *                                     → DEPLOYING
 *                                     → FAILED
 *
 * Legacy boolean flags (isProcessing, hasCompletedBuild, autoCreating)
 * are derived from this phase for backward compatibility.
 */
export type BuildPhase =
  | 'idle'
  | 'confirming'   // Template confirmation modal open
  | 'initializing'  // Auto-creating project/build (before agents start)
  | 'building'      // Agents actively working
  | 'completed'     // Build done, preview available
  | 'iterating'     // Follow-up build in progress
  | 'deploying'     // Deployment in progress
  | 'failed'        // Build failed

export interface FileDiffEntry {
  status: 'added' | 'modified' | 'deleted' | 'unchanged'
  diff?: string
  lines_added?: number
  lines_removed?: number
  old_content?: string
}

export type BuildDiff = Record<string, FileDiffEntry>

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: string
  artifacts?: Record<string, string>
  workflowSummary?: Record<string, unknown>
  giteaWebUrl?: string
}

export interface ActiveAgent {
  status: 'running' | 'completed' | 'failed' | 'retrying'
  task: string
  startedAt: string
  completedAt?: string
  costUsd?: number
  /** Plan 28.6 — error classification surfaced from agent_complete WS event. */
  errorClass?: 'recoverable' | 'fatal'
  /** Plan 28.6 — current attempt number, 1-indexed. */
  attempt?: number
  /** Plan 28.6 — maximum attempts allowed before flipping to `failed`. */
  maxAttempts?: number
  /** Plan 28.6 — name of deterministic fixer that may help (hint). */
  fixer?: string
  /** Plan 28.6 — last error message, surfaced in retry/fatal cards. */
  errorMessage?: string
}

export interface BuildLog {
  message: string
  level: string
  agent: string
  ts: string
}

/** Phase F (D35): Error transparency — individual error log entry */
export interface BuildErrorEntry {
  type: string
  file: string
  message: string
  fixed: boolean
  timestamp: number
}

export interface ActivityEntry {
  id: string
  type: 'agent_start' | 'agent_complete' | 'agent_failed' | 'handover' | 'log' | 'input' | 'output'
  agentName: string
  agentId: string
  taskId: string
  message: string
  level: string
  timestamp: string
  detail?: string
  artifacts?: Record<string, string>
  duration?: number
  model?: string
  costUsd?: number
}

/** P6-T3: Drag-and-drop state */
export interface DragState {
  dragging: boolean
  sourceId: string | null
  targetId: string | null
  position: 'before' | 'after' | 'inside' | null
}

/** P6-T3: DnD history entry for undo/redo */
export interface DndHistoryEntry {
  type: 'move' | 'add'
  componentId: string
  targetId: string
  position: 'before' | 'after' | 'inside'
  template?: string
  /** Populated by bridge ve:move-complete for undo of moves */
  previousParentId?: string | null
  previousNextSiblingId?: string | null
  timestamp: number
}

/** P6-T4: Figma import layer entry */
export interface FigmaImportLayer {
  id: string
  name: string
  type: string
  element: string
  selected: boolean
  depth: number
  preview?: string
}

/** P6-T4: Figma import state */
export interface FigmaImportState {
  id: string
  layers: FigmaImportLayer[]
  status: 'loading' | 'preview' | 'importing' | 'done' | 'error'
  error?: string
}

/** P6-T1: Visual editor types */
export interface SelectedComponent {
  id: string
  name: string
  filePath: string
  line: number
  props: Record<string, unknown>
  tailwindClasses: string[]
  layout: { display: string; direction?: string; gap?: string; align?: string; justify?: string }
  boundingRect: { x: number; y: number; width: number; height: number }
  children: string[]
}

export interface ComponentTreeNode {
  id: string
  name: string
  filePath: string
  line: number
  children: ComponentTreeNode[]
  depth: number
}

interface BuildState {
  /** Single source of truth for build lifecycle. Use setBuildPhase() for transitions. */
  buildPhase: BuildPhase
  setBuildPhase: (phase: BuildPhase) => void

  messages: ChatMessage[]
  activeAgents: Record<string, ActiveAgent>
  taskGraph: TaskGraph | null
  completedTaskIds: Set<string>
  generatedFiles: Record<string, string>
  isProcessing: boolean
  currentBuildId: string | null
  /** Real build_context_id (UUID) from the database — used for Inspector API calls */
  buildContextId: string | null
  currentBuildNumber: number
  logs: BuildLog[]
  activityLog: ActivityEntry[]
  previewUrl: string | null
  /** The E2B sandbox URL behind a branded previewUrl that redirects to it; the
   *  visual-editor bridge trusts exactly this origin (VE-1). null = previewUrl's own. */
  previewSandboxUrl: string | null
  projectId: string | null
  applicationId: string | null
  previewLogs: Array<{ service: string; line: string }>
  previewRefreshKey: number
  buildFailedMessage: string | null
  hasCompletedBuild: boolean

  /** Plan 28 / Agent B8 — preview + deploy lifecycle signals from the
   * backend. These let `FiveStageProgress` flip the Preview and Deploy
   * chips between pending / active / complete / failed without having
   * to read project status from the DB. */
  previewStarting: boolean
  /** Plan 28.7 / Agent D9 — backend executor flavour from preview_starting:
   *   - 'e2b'      : E2B Firecracker sandbox.
   *   - 'docker'   : local Docker on the API host.
   *   - 'host'     : legacy generic host runtime (kept for back-compat).
   *   - 'dispatch' : preview-agent v2 (table-driven on Hetzner).
   *   - 'ssh'      : SSH fallback when no preview-agent is alive.
   * `null` means we haven't seen a `preview_starting` yet for this build. */
  previewRuntime: 'e2b' | 'docker' | 'host' | 'dispatch' | 'ssh' | null
  /** Plan 28.7 / Agent D9 — flips on when the backend broadcasts
   * `preview_no_hosts_available`. Surface as a "preview backend
   * unavailable" badge so the user knows the spinner isn't a hang. */
  previewNoHostsAvailable: boolean
  previewFailedMessage: string | null
  deployStarted: boolean
  deployCompleted: boolean
  deployFailedMessage: string | null
  deploymentUrl: string | null
  setPreviewStarting: (v: boolean) => void
  setPreviewRuntime: (
    v: 'e2b' | 'docker' | 'host' | 'dispatch' | 'ssh' | null,
  ) => void
  setPreviewNoHostsAvailable: (v: boolean) => void
  setPreviewFailedMessage: (msg: string | null) => void
  setDeployStarted: (v: boolean) => void
  setDeployCompleted: (v: boolean, url?: string | null) => void
  /** Set only the deployment URL (no deployCompleted side effects) — used to
   *  hydrate the live URL from deploy history on page reload. */
  setDeploymentUrl: (url: string | null) => void
  setDeployFailedMessage: (msg: string | null) => void
  buildDiff: BuildDiff | null
  routingIntent: string | null
  routingInProgress: boolean

  /** Phase F (D35): Error transparency — counts and detailed log */
  errorsFound: number
  errorsFixed: number
  errorLog: BuildErrorEntry[]

  /** Phase F (D28): Cost counter and ETA */
  currentCost: number
  estimatedEta: number

  /** E4 (D70): Quality score and verification pipeline state */
  qualityScore: number | null
  qualityGrade: string | null
  verificationInProgress: boolean

  /** P2-T2: File streaming — track per-file creation events */
  fileCreatedEvents: Array<{ path: string; size: number; timestamp: number }>
  addFileCreatedEvent: (path: string, size: number) => void

  /** P2-T4: Security scan findings */
  securityFindings: Array<{ file: string; line: number; message: string; severity: string }>
  securitySummary: { total: number; critical: number; high: number; medium: number; low: number } | null
  setSecurityFindings: (findings: Array<{ file: string; line: number; message: string; severity: string }>, summary: { total: number; critical: number; high: number; medium: number; low: number } | null) => void

  /** P2-T3: Browser validation results */
  browserValidation: { score: number; findings: Array<{ category: string; message: string; severity: string }> } | null
  setBrowserValidation: (results: { score: number; findings: Array<{ category: string; message: string; severity: string }> } | null) => void

  /** P5-T7: Device simulator */
  selectedDevice: string
  orientation: 'portrait' | 'landscape'
  setSelectedDevice: (device: string) => void
  setOrientation: (orientation: 'portrait' | 'landscape') => void
  toggleOrientation: () => void

  /** P5: Current stack for the active build (used by DeviceSimulator auto-detection) */
  currentStack: string | null
  setCurrentStack: (stack: string | null) => void

  /** P6-T1: Visual editor — component selection & inspection */
  selectionMode: boolean
  selectedComponent: SelectedComponent | null
  componentTree: ComponentTreeNode[]
  hoveredComponent: string | null
  setSelectionMode: (enabled: boolean) => void
  setSelectedComponent: (component: SelectedComponent | null) => void
  setComponentTree: (tree: ComponentTreeNode[]) => void
  setHoveredComponent: (id: string | null) => void

  /** P6-T3: Drag-and-drop layout editing */
  dragState: DragState
  setDragState: (state: Partial<DragState>) => void

  /** P6-T3: DnD undo/redo stacks */
  dndUndoStack: DndHistoryEntry[]
  dndRedoStack: DndHistoryEntry[]
  pushDndEntry: (entry: Omit<DndHistoryEntry, 'timestamp'>) => void
  updateLastDndEntry: (update: Partial<DndHistoryEntry>) => void
  undoDndChange: () => DndHistoryEntry | null
  redoDndChange: () => DndHistoryEntry | null
  clearDndChanges: () => void

  /** P6-T2: Inline property editing — pending style changes with undo/redo */
  pendingStyleChanges: Record<string, Record<string, string>>
  styleUndoStack: Array<{ componentId: string; classes: string[]; styles: Record<string, string>; file: string; code: string }>
  styleRedoStack: Array<{ componentId: string; classes: string[]; styles: Record<string, string>; file: string; code: string }>
  applyStyleChange: (componentId: string, property: string, value: string) => void
  pushStyleEntry: (entry: { componentId: string; classes: string[]; styles: Record<string, string>; file: string; code: string }) => void
  undoStyleChange: () => { componentId: string; classes: string[]; styles: Record<string, string> } | null
  redoStyleChange: () => { componentId: string; classes: string[]; styles: Record<string, string> } | null
  getLatestEntryForComponent: (componentId: string) => { classes: string[]; styles: Record<string, string> } | null
  clearStyleChanges: () => void

  /** P6-T7: Review & approval workflow — pending visual review */
  pendingReview: {
    id: string
    changes: Array<{
      id: string
      componentId: string
      type: string
      before: string
      after: string
      status: 'pending' | 'approved' | 'rejected'
    }>
  } | null
  setPendingReview: (review: BuildState['pendingReview']) => void

  /** P6-T6: Contextual AI chat */
  visualChatOpen: boolean
  visualChatMessages: Array<{ id: string; role: 'user' | 'assistant'; content: string; componentId?: string; codeChanges?: Array<{ file: string; before: string; after: string }> }>
  setVisualChatOpen: (open: boolean) => void
  addVisualChatMessage: (msg: Omit<BuildState['visualChatMessages'][0], 'id'>) => void
  clearVisualChat: () => void

  /** P6-T4: Figma import state */
  figmaImport: FigmaImportState | null
  setFigmaImport: (state: FigmaImportState | null) => void

  /** P6-T8: Version history — time travel for visual edits */
  versions: Array<{ id: string; description: string; author: string; createdAt: string; isCurrent: boolean }>
  selectedVersionId: string | null
  setVersions: (versions: BuildState['versions']) => void
  setSelectedVersionId: (id: string | null) => void

  /** P6-T5: Multiplayer collaboration */
  collaborators: Array<{
    id: string
    name: string
    color: string
    cursor: { x: number; y: number } | null
    selectedComponentId: string | null
    isIdle: boolean
  }>
  setCollaborators: (collaborators: BuildState['collaborators']) => void

  /** Phase 1: Zero-friction onboarding — auto-create in progress */
  autoCreating: boolean
  setAutoCreating: (v: boolean) => void

  /** Detected domain from prompt analysis (landing, dashboard, ecommerce, etc.) */
  detectedDomain: string | null
  /** Post-build suggestion chips shown in Simple mode */
  postBuildSuggestions: string[]
  setDetectedDomain: (domain: string | null) => void
  setPostBuildSuggestions: (suggestions: string[]) => void

  setBuildDiff: (diff: BuildDiff | null) => void
  addMessage: (msg: ChatMessage) => void
  setProcessing: (processing: boolean) => void
  setCurrentBuildId: (id: string | null) => void
  setBuildContextId: (id: string | null) => void
  setCurrentBuildNumber: (n: number) => void
  /** A new url always replaces the sandbox url too (omitted = none). */
  setPreviewUrl: (url: string | null, sandboxUrl?: string | null) => void
  setBuildFailedMessage: (message: string | null) => void
  setHasCompletedBuild: (v: boolean) => void
  setProjectId: (id: string | null) => void
  setApplicationId: (id: string | null) => void
  agentStarted: (name: string, task: string, taskId?: string, model?: string, inputSummary?: string) => void
  agentCompleted: (name: string, data?: Record<string, unknown>) => void
  /**
   * Plan 28.6 — `classification` carries error_class / attempt / max_attempts
   * / fixer pulled from the WS `agent_complete` payload. All fields are
   * optional so legacy callers (no classification metadata) keep working.
   */
  agentFailed: (
    name: string,
    error?: string,
    classification?: {
      errorClass?: 'recoverable' | 'fatal'
      attempt?: number
      maxAttempts?: number
      fixer?: string
      errorMessage?: string
    },
  ) => void
  enrichAgentDetail: (agentId: string, content: string, kind: 'input' | 'output' | 'artifacts') => void
  setTaskGraph: (graph: TaskGraph) => void
  markSubtaskComplete: (taskId: string) => void
  markSubtaskRunning: (taskId: string) => void
  addLog: (log: BuildLog) => void
  setActivityLog: (entries: ActivityEntry[]) => void
  addActivity: (entry: Omit<ActivityEntry, 'id'>) => void
  setFiles: (files: Record<string, string>) => void
  clearFiles: () => void
  addPreviewLog: (line: string, service?: string) => void
  clearPreviewLogs: () => void
  triggerPreviewRefresh: () => void
  setRoutingIntent: (intent: string | null) => void
  setRoutingInProgress: (v: boolean) => void

  /** Phase F (D35): Error transparency actions */
  addError: (error: Omit<BuildErrorEntry, 'timestamp'>) => void
  markErrorFixed: (index: number) => void
  resetErrors: () => void

  /** Phase F (D28): Cost counter and ETA actions */
  updateCost: (cost: number) => void
  updateEta: (eta: number) => void

  /** E4 (D70): Quality and verification actions */
  setQualityScore: (score: number | null, grade?: string | null) => void
  setVerificationInProgress: (v: boolean) => void

  /** Claim flow state */
  claimErrorReason: string | null
  claimRetryCount: number
  claimInProgress: boolean
  claimJustCompleted: boolean
  pendingAuthAction: string | null
  marketingAssets: Record<string, unknown> | null
  currentStage: string | null
  /** Auto-repair UI state — drives the inline banner in DeployPanel. */
  autoRepair: AutoRepairState | null
  setClaimError: (reason: string) => void
  clearClaimError: () => void
  incrementClaimRetry: () => void
  setClaimInProgress: (v: boolean) => void
  setClaimJustCompleted: (v: boolean) => void
  setPendingAuthAction: (action: string | null) => void
  setMarketingAssets: (assets: Record<string, unknown>) => void
  setCurrentStage: (stage: string | null) => void
  setAutoRepair: (state: AutoRepairState | null) => void

  reset: () => void
}

export type AutoRepairState =
  | {
      phase: 'started'
      trigger: 'deploy_failed' | 'live_url_unreachable' | string
      reason: string
      step?: string
      attemptsAllowed: number
    }
  | {
      phase: 'progress'
      step: 'loading_files' | 'generating_patch' | 'applying_patch' | string
      filesInContext?: number
      filesChanged?: number
    }
  | {
      phase: 'complete'
      rationale: string
      filesChanged: number
      changedPaths: string[]
      costUsd: number
      needsRedeploy: boolean
    }
  | {
      phase: 'failed'
      error: string
      rationale?: string
      costUsd?: number
    }

const MAX_ACTIVITY = 500

export const useBuildStore = create<BuildState>((set, get) => ({
  // Build phase state machine — single source of truth
  buildPhase: 'idle' as BuildPhase,
  setBuildPhase: (phase) => {
    const prev = get().buildPhase
    // Validate transitions
    const valid: Record<BuildPhase, BuildPhase[]> = {
      idle: ['confirming', 'initializing', 'building'],
      confirming: ['idle', 'initializing', 'building'],
      initializing: ['building', 'failed', 'idle'],
      building: ['completed', 'failed', 'idle'],
      completed: ['iterating', 'deploying', 'idle', 'confirming'],
      iterating: ['completed', 'failed', 'idle'],
      deploying: ['completed', 'idle', 'failed'],
      failed: ['idle', 'confirming', 'initializing', 'building'],
    }
    if (!valid[prev]?.includes(phase)) {
      console.warn(`[buildStore] Invalid phase transition: ${prev} → ${phase}`)
      // Allow it anyway — log but don't block (safety during migration)
    }
    set({ buildPhase: phase })
  },

  messages: [],
  activeAgents: {},
  taskGraph: null,
  completedTaskIds: new Set(),
  generatedFiles: {},
  isProcessing: false,
  currentBuildId: null,
  buildContextId: null,
  currentBuildNumber: 0,
  logs: [],
  activityLog: [],
  previewUrl: null,
  previewSandboxUrl: null,
  projectId: null,
  applicationId: null,
  previewLogs: [],
  previewRefreshKey: 0,
  buildFailedMessage: null,
  hasCompletedBuild: false,
  buildDiff: null,
  routingIntent: null,
  routingInProgress: false,

  // Plan 28 / Agent B8 — preview + deploy lifecycle signals
  previewStarting: false,
  // Plan 28.7 / Agent D9 — backend executor flavour + no-hosts state.
  previewRuntime: null,
  previewNoHostsAvailable: false,
  previewFailedMessage: null,
  deployStarted: false,
  deployCompleted: false,
  deployFailedMessage: null,
  deploymentUrl: null,
  setPreviewStarting: (v) => set({ previewStarting: v }),
  setPreviewRuntime: (v) => set({ previewRuntime: v }),
  setPreviewNoHostsAvailable: (v) => set({ previewNoHostsAvailable: v }),
  setPreviewFailedMessage: (msg) => set({ previewFailedMessage: msg }),
  setDeployStarted: (v) =>
    set((s) =>
      v
        ? {
            deployStarted: true,
            deployCompleted: false,
            deployFailedMessage: null,
            buildPhase: s.buildPhase === 'completed' ? 'deploying' : s.buildPhase,
          }
        : { deployStarted: false }
    ),
  setDeployCompleted: (v, url) =>
    set((s) =>
      v
        ? {
            deployStarted: false,
            deployCompleted: true,
            deployFailedMessage: null,
            deploymentUrl: url ?? s.deploymentUrl,
            buildPhase: s.buildPhase === 'deploying' ? 'completed' : s.buildPhase,
          }
        : { deployCompleted: false }
    ),
  setDeploymentUrl: (url) => set({ deploymentUrl: url }),
  setDeployFailedMessage: (msg) =>
    set((s) => ({
      deployFailedMessage: msg,
      deployStarted: msg ? false : s.deployStarted,
      buildPhase: msg && s.buildPhase === 'deploying' ? 'failed' : s.buildPhase,
    })),

  // Phase F (D35): Error transparency
  errorsFound: 0,
  errorsFixed: 0,
  errorLog: [],

  // Phase F (D28): Cost counter and ETA
  currentCost: 0,
  estimatedEta: 0,

  // E4 (D70): Quality and verification state
  qualityScore: null,
  qualityGrade: null,
  verificationInProgress: false,

  // P2-T2: File streaming events
  fileCreatedEvents: [],
  addFileCreatedEvent: (path, size) =>
    set((s) => ({
      fileCreatedEvents: [...s.fileCreatedEvents.slice(-200), { path, size, timestamp: Date.now() }],
    })),

  // P2-T4: Security scan findings
  securityFindings: [],
  securitySummary: null,
  setSecurityFindings: (findings, summary) => set({ securityFindings: findings, securitySummary: summary }),

  // P2-T3: Browser validation results
  browserValidation: null,
  setBrowserValidation: (results) => set({ browserValidation: results }),

  // P5-T7: Device simulator
  selectedDevice: 'desktop',
  orientation: 'portrait',
  setSelectedDevice: (device) => set({ selectedDevice: device }),
  setOrientation: (orientation) => set({ orientation }),
  toggleOrientation: () =>
    set((s) => ({ orientation: s.orientation === 'portrait' ? 'landscape' : 'portrait' })),

  // P5: Current stack tracking
  currentStack: null,
  setCurrentStack: (stack) => set({ currentStack: stack }),

  // P6-T1: Visual editor — component selection
  selectionMode: false,
  selectedComponent: null,
  componentTree: [],
  hoveredComponent: null,
  setSelectionMode: (enabled) => set({ selectionMode: enabled, selectedComponent: enabled ? null : null }),
  setSelectedComponent: (component) => set({ selectedComponent: component }),
  setComponentTree: (tree) => set({ componentTree: tree }),
  setHoveredComponent: (id) => set({ hoveredComponent: id }),

  // P6-T3: Drag-and-drop layout editing
  dragState: { dragging: false, sourceId: null, targetId: null, position: null },
  setDragState: (partial) =>
    set((s) => ({ dragState: { ...s.dragState, ...partial } })),

  // P6-T3: DnD undo/redo stacks
  dndUndoStack: [],
  dndRedoStack: [],
  pushDndEntry: (entry) =>
    set((s) => ({
      dndUndoStack: [...s.dndUndoStack, { ...entry, timestamp: Date.now() }],
      dndRedoStack: [],
    })),
  updateLastDndEntry: (update) =>
    set((s) => {
      if (s.dndUndoStack.length === 0) return {}
      const stack = [...s.dndUndoStack]
      stack[stack.length - 1] = { ...stack[stack.length - 1], ...update }
      return { dndUndoStack: stack }
    }),
  undoDndChange: () => {
    const s = get()
    if (s.dndUndoStack.length === 0) return null
    const stack = [...s.dndUndoStack]
    const entry = stack.pop()!
    set({ dndUndoStack: stack, dndRedoStack: [...s.dndRedoStack, entry] })
    return entry
  },
  redoDndChange: () => {
    const s = get()
    if (s.dndRedoStack.length === 0) return null
    const stack = [...s.dndRedoStack]
    const entry = stack.pop()!
    set({ dndRedoStack: stack, dndUndoStack: [...s.dndUndoStack, entry] })
    return entry
  },
  clearDndChanges: () => set({ dndUndoStack: [], dndRedoStack: [] }),

  // P6-T2: Inline property editing — pending style changes with undo/redo
  pendingStyleChanges: {},
  styleUndoStack: [],
  styleRedoStack: [],
  applyStyleChange: (componentId, property, value) =>
    set((s) => ({
      pendingStyleChanges: {
        ...s.pendingStyleChanges,
        [componentId]: {
          ...(s.pendingStyleChanges[componentId] || {}),
          [property]: value,
        },
      },
    })),
  pushStyleEntry: (entry) =>
    set((s) => {
      const newStack = [...s.styleUndoStack, entry]
      // Auto-populate pendingReview from style changes (P6-T7 wiring)
      const changeId = `change_${entry.componentId}_${newStack.length}`
      const existingReview = s.pendingReview
      const newChange = {
        id: changeId,
        componentId: entry.componentId,
        type: 'style',
        before: JSON.stringify({}),
        after: JSON.stringify(entry.styles),
        status: 'pending' as const,
      }
      const reviewId = existingReview?.id || `review_${Date.now()}`
      const updatedChanges = existingReview
        ? [...existingReview.changes, newChange]
        : [newChange]
      return {
        styleUndoStack: newStack,
        styleRedoStack: [],
        pendingReview: { id: reviewId, changes: updatedChanges },
      }
    }),
  undoStyleChange: () => {
    const s = get()
    if (s.styleUndoStack.length === 0) return null
    const stack = [...s.styleUndoStack]
    const entry = stack.pop()!
    set({ styleUndoStack: stack, styleRedoStack: [...s.styleRedoStack, entry] })
    // Return the latest remaining entry for same component (to re-apply), or null
    const prev = [...stack].reverse().find((e) => e.componentId === entry.componentId)
    return prev ? { componentId: entry.componentId, classes: prev.classes, styles: prev.styles } : { componentId: entry.componentId, classes: [], styles: {} }
  },
  redoStyleChange: () => {
    const s = get()
    if (s.styleRedoStack.length === 0) return null
    const stack = [...s.styleRedoStack]
    const entry = stack.pop()!
    set({ styleRedoStack: stack, styleUndoStack: [...s.styleUndoStack, entry] })
    return { componentId: entry.componentId, classes: entry.classes, styles: entry.styles }
  },
  getLatestEntryForComponent: (componentId) => {
    const s = get()
    const entry = [...s.styleUndoStack].reverse().find((e) => e.componentId === componentId)
    return entry ? { classes: entry.classes, styles: entry.styles } : null
  },
  clearStyleChanges: () => set({ pendingStyleChanges: {}, styleUndoStack: [], styleRedoStack: [], dndUndoStack: [], dndRedoStack: [], pendingReview: null }),

  // P6-T7: Review & approval workflow
  pendingReview: null,
  setPendingReview: (review) => set({ pendingReview: review }),

  // P6-T6: Contextual AI chat
  visualChatOpen: false,
  visualChatMessages: [],
  setVisualChatOpen: (open) => set({ visualChatOpen: open }),
  addVisualChatMessage: (msg) =>
    set((s) => ({
      visualChatMessages: [
        ...s.visualChatMessages,
        { ...msg, id: crypto.randomUUID() },
      ],
    })),
  clearVisualChat: () => set({ visualChatMessages: [] }),

  // P6-T4: Figma import
  figmaImport: null,
  setFigmaImport: (state) => set({ figmaImport: state }),

  // P6-T8: Version history — time travel
  versions: [],
  selectedVersionId: null,
  setVersions: (versions) => set({ versions }),
  setSelectedVersionId: (id) => set({ selectedVersionId: id }),

  // P6-T5: Multiplayer collaboration
  collaborators: [],
  setCollaborators: (collaborators) => set({ collaborators }),

  // Phase 1: Zero-friction onboarding
  autoCreating: false,
  setAutoCreating: (v) => set((s) => ({
    autoCreating: v,
    // Sync buildPhase — auto-creating = initializing phase
    buildPhase: v ? 'initializing' : s.buildPhase,
  })),

  detectedDomain: null,
  postBuildSuggestions: [],
  setDetectedDomain: (domain) => set({ detectedDomain: domain }),
  setPostBuildSuggestions: (suggestions) => set({ postBuildSuggestions: suggestions }),

  setBuildDiff: (diff) => set({ buildDiff: diff }),
  addMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),

  setProcessing: (processing) =>
    set((s) => ({
      isProcessing: processing,
      // Sync buildPhase — if starting processing, move to building/iterating
      buildPhase: processing
        ? (s.hasCompletedBuild ? 'iterating' : 'building')
        : s.buildPhase,
      // Reset only the lightweight transient fields when entering processing.
      // Do NOT wipe `generatedFiles` or `buildDiff` here — when the WebSocket
      // reconnects and replays events for an already-completed build, the
      // first `build_started` frame would otherwise erase the files we just
      // seeded from /api/build/{id}/files and freeze the Files tab on its
      // empty state. Callers that legitimately start a NEW build (prompt
      // submit, anon-promote, etc.) wipe these explicitly via clearFiles().
      ...(processing
        ? {
            buildFailedMessage: null,
            currentCost: 0,
            estimatedEta: 0,
          }
        : {}),
    })),

  setCurrentBuildId: (id) => set({ currentBuildId: id }),
  setBuildContextId: (id) => set({ buildContextId: id }),

  setCurrentBuildNumber: (n) => set({ currentBuildNumber: n }),

  setPreviewUrl: (url, sandboxUrl) => set({
    previewUrl: url,
    previewSandboxUrl: url && sandboxUrl ? sandboxUrl : null,
  }),

  setBuildFailedMessage: (message) => set((s) => ({
    buildFailedMessage: message,
    // If setting a failure message, transition to failed phase
    buildPhase: message ? 'failed' : s.buildPhase,
  })),

  setHasCompletedBuild: (v) =>
    set((s) => ({
      hasCompletedBuild: v,
      // Sync buildPhase — completed build = completed phase (unless already deploying)
      buildPhase: v && s.buildPhase !== 'deploying' ? 'completed' : s.buildPhase,
    })),

  setProjectId: (id) => set({ projectId: id }),

  setApplicationId: (id) => set({ applicationId: id }),

  agentStarted: (name, task, taskId, model, inputSummary) =>
    set((s) => {
      // Build initial detail from task description + input summary
      let detail = task
      if (inputSummary) {
        detail += '\n--- Input ---\n' + inputSummary
      }
      const entry: ActivityEntry = {
        id: crypto.randomUUID(),
        type: 'agent_start',
        agentName: name,
        agentId: name,
        taskId: taskId || '',
        message: `${name} started: ${task}`,
        level: 'INFO',
        timestamp: new Date().toISOString(),
        detail,
        model,
      }
      return {
        activeAgents: {
          ...s.activeAgents,
          [name]: { status: 'running', task, startedAt: new Date().toISOString() },
        },
        activityLog: [...s.activityLog, entry].slice(-MAX_ACTIVITY),
      }
    }),

  agentCompleted: (name, data) =>
    set((s) => {
      const agent = s.activeAgents[name]
      const duration = agent ? Date.now() - new Date(agent.startedAt).getTime() : undefined
      const artifacts = data?.artifacts as Record<string, string> | undefined
      const outputSummary = (data?.output_summary as string) || ''
      const costUsd = (data?.cost_usd as number) || (data?.amount_deducted as number) || 0
      // Plan 28.6 — extract classification fields from agent_complete payload.
      const wsStatus = data?.status as 'success' | 'failed' | 'retrying' | undefined
      const errorClass = data?.error_class as 'recoverable' | 'fatal' | undefined
      const fixer = (data?.fixer as string) || undefined
      const attempt = typeof data?.attempt === 'number' ? (data?.attempt as number) : undefined
      const maxAttempts =
        typeof data?.max_attempts === 'number' ? (data?.max_attempts as number) : undefined
      const errorMessage = (data?.error_message as string) || undefined
      // If the backend signalled retrying, branch into the retry-update path
      // (we never flip the activeAgent to `completed`).
      if (wsStatus === 'retrying') {
        const progressMsg =
          attempt && maxAttempts
            ? `${name} retrying (attempt ${attempt}/${maxAttempts})…`
            : `${name} retrying…`
        const retryEntry: ActivityEntry = {
          id: crypto.randomUUID(),
          type: 'log',
          agentName: name,
          agentId: name,
          taskId: (data?.task_id as string) || '',
          message: progressMsg,
          level: 'WARNING',
          timestamp: new Date().toISOString(),
          detail: errorMessage,
        }
        return {
          activeAgents: {
            ...s.activeAgents,
            [name]: {
              ...s.activeAgents[name],
              status: 'retrying',
              errorClass,
              attempt,
              maxAttempts,
              fixer,
              errorMessage,
            },
          },
          activityLog: [...s.activityLog, retryEntry].slice(-MAX_ACTIVITY),
        }
      }
      // Extract test result counts from output_summary for tester agents
      const testMatch = outputSummary.match(/Tests:\s*(\d+)\/(\d+)\s*passed/)
      let completedMsg = `${name} completed${duration ? ` (${(duration / 1000).toFixed(1)}s)` : ''}`
      if (testMatch) {
        completedMsg += ` — Tests: ${testMatch[1]}/${testMatch[2]} passed`
      } else if (outputSummary.startsWith('FAILED:')) {
        completedMsg += ` — ${outputSummary.slice(0, 80)}`
      }
      // Update the original agent_start entry in-place (stop the spinner)
      const idx = [...s.activityLog].reverse().findIndex(
        (e) => e.agentId === name && e.type === 'agent_start'
      )
      let updatedLog: ActivityEntry[]
      if (idx !== -1) {
        const realIdx = s.activityLog.length - 1 - idx
        updatedLog = s.activityLog.map((e, i) => {
          if (i !== realIdx) return e
          // Append output summary to existing detail
          let detail = e.detail || ''
          if (outputSummary) {
            detail += '\n--- Output ---\n' + outputSummary
          }
          if (artifacts && Object.keys(artifacts).length > 0) {
            detail += '\n--- Files ---\n' + Object.keys(artifacts).join('\n')
          }
          return {
            ...e,
            type: 'agent_complete' as const,
            message: completedMsg,
            level: 'SUCCESS',
            duration,
            detail,
            artifacts: artifacts || e.artifacts,
            costUsd: costUsd || e.costUsd,
          }
        })
      } else {
        // Fallback: no matching start entry found — append new entry
        const entry: ActivityEntry = {
          id: crypto.randomUUID(),
          type: 'agent_complete',
          agentName: name,
          agentId: name,
          taskId: (data?.task_id as string) || '',
          message: completedMsg,
          level: 'SUCCESS',
          timestamp: new Date().toISOString(),
          duration,
          artifacts,
          costUsd,
        }
        updatedLog = [...s.activityLog, entry]
      }
      return {
        activeAgents: {
          ...s.activeAgents,
          [name]: {
            ...s.activeAgents[name],
            status: 'completed',
            completedAt: new Date().toISOString(),
            costUsd,
          },
        },
        activityLog: updatedLog.slice(-MAX_ACTIVITY),
      }
    }),

  agentFailed: (name, error, classification) =>
    set((s) => {
      // Update the original agent_start entry in-place (stop the spinner)
      const idx = [...s.activityLog].reverse().findIndex(
        (e) => e.agentId === name && e.type === 'agent_start'
      )
      let updatedLog: ActivityEntry[]
      if (idx !== -1) {
        const realIdx = s.activityLog.length - 1 - idx
        updatedLog = s.activityLog.map((e, i) =>
          i === realIdx
            ? {
                ...e,
                type: 'agent_failed' as const,
                message: `${name} failed${error ? `: ${error}` : ''}`,
                level: 'ERROR',
                detail: error || e.detail,
              }
            : e
        )
      } else {
        const entry: ActivityEntry = {
          id: crypto.randomUUID(),
          type: 'agent_failed',
          agentName: name,
          agentId: name,
          taskId: '',
          message: `${name} failed${error ? `: ${error}` : ''}`,
          level: 'ERROR',
          timestamp: new Date().toISOString(),
          detail: error,
        }
        updatedLog = [...s.activityLog, entry]
      }
      return {
        activeAgents: {
          ...s.activeAgents,
          [name]: {
            ...s.activeAgents[name],
            status: 'failed',
            completedAt: new Date().toISOString(),
            errorClass: classification?.errorClass,
            attempt: classification?.attempt,
            maxAttempts: classification?.maxAttempts,
            fixer: classification?.fixer,
            errorMessage: classification?.errorMessage ?? error,
          },
        },
        activityLog: updatedLog.slice(-MAX_ACTIVITY),
      }
    }),

  setTaskGraph: (graph) =>
    set((s) => {
      if (!s.taskGraph) {
        // First graph ever — tag all tasks with build_number = 1
        const buildNum = Math.max(s.currentBuildNumber, 1)
        const taggedTasks = graph.tasks.map((t) => ({
          ...t,
          build_number: t.build_number ?? buildNum,
        }))
        return {
          taskGraph: { ...graph, tasks: taggedTasks },
          currentBuildNumber: buildNum,
        }
      }
      // Follow-up graph — only increment build number if there are genuinely new tasks
      // (prevents double-increment when re-hydrating the same graph from DB)
      const oldById = new Map(s.taskGraph.tasks.map((t) => [t.id, t]))
      const hasNewTasks = graph.tasks.some((t) => !oldById.has(t.id))
      const nextBuild = hasNewTasks ? s.currentBuildNumber + 1 : s.currentBuildNumber
      const merged = new Map(oldById)
      for (const t of graph.tasks) {
        const existing = merged.get(t.id)
        // Preserve completed tasks from old graph
        if (existing && (existing.status === 'completed' || s.completedTaskIds.has(t.id))) {
          continue
        }
        merged.set(t.id, { ...t, build_number: t.build_number ?? nextBuild })
      }
      return {
        taskGraph: {
          tasks: Array.from(merged.values()),
          total_complexity: graph.total_complexity,
          estimated_total_duration_minutes: graph.estimated_total_duration_minutes,
        },
        currentBuildNumber: nextBuild,
      }
    }),

  markSubtaskComplete: (taskId) =>
    set((s) => {
      const next = new Set(s.completedTaskIds)
      next.add(taskId)
      let updatedGraph = s.taskGraph
      if (updatedGraph) {
        updatedGraph = {
          ...updatedGraph,
          tasks: updatedGraph.tasks.map((t) =>
            t.id === taskId ? { ...t, status: 'completed' as const } : t
          ),
        }
      }
      return { completedTaskIds: next, taskGraph: updatedGraph }
    }),

  markSubtaskRunning: (taskId) =>
    set((s) => {
      if (!s.taskGraph) return {}
      return {
        taskGraph: {
          ...s.taskGraph,
          tasks: s.taskGraph.tasks.map((t) =>
            t.id === taskId && t.status === 'pending'
              ? { ...t, status: 'running' as const }
              : t
          ),
        },
      }
    }),

  addLog: (log) =>
    set((s) => {
      const entry: ActivityEntry = {
        id: crypto.randomUUID(),
        type: log.level === 'INPUT' ? 'input' : log.level === 'OUTPUT' ? 'output' : 'log',
        agentName: log.agent || '',
        agentId: log.agent || '',
        taskId: '',
        message: log.message,
        level: log.level,
        timestamp: log.ts || new Date().toISOString(),
      }
      return {
        logs: [...s.logs.slice(-200), log],
        activityLog: [...s.activityLog, entry].slice(-MAX_ACTIVITY),
      }
    }),

  setActivityLog: (entries) =>
    set(() => ({ activityLog: entries.slice(-MAX_ACTIVITY) })),

  addActivity: (entry) =>
    set((s) => {
      // Stop the perpetual spinner: when a caller appends an
      // `agent_complete` / `agent_failed` entry, ALSO flip the matching
      // prior `agent_start` row (same agentId) to the terminal type.
      // Without this, the BuildPage `phaseRow()` calls leave the
      // "Planning the build…" / "Writing files…" / "Verifying…" rows
      // spinning forever even after their paired "Plan ready."
      // / "Files written." / "Verification complete." rows arrive
      // (reported with screenshot 2026-05-20). The `agentCompleted`
      // store action already does this for the v1 agent path; this
      // makes plain `addActivity` callers behave the same.
      const isTerminal = entry.type === 'agent_complete' || entry.type === 'agent_failed'
      let log = s.activityLog
      if (isTerminal && entry.agentId) {
        const reverseIdx = [...log]
          .reverse()
          .findIndex((e) => e.agentId === entry.agentId && e.type === 'agent_start')
        if (reverseIdx !== -1) {
          const realIdx = log.length - 1 - reverseIdx
          log = log.map((e, i) =>
            i === realIdx ? { ...e, type: entry.type, level: entry.level ?? e.level } : e,
          )
        }
      }
      return {
        activityLog: [...log, { ...entry, id: crypto.randomUUID() }].slice(-MAX_ACTIVITY),
      }
    }),

  enrichAgentDetail: (agentId, content, kind) =>
    set((s) => {
      // Find the most recent agent_start (or agent_complete) entry for this agent
      const idx = [...s.activityLog].reverse().findIndex(
        (e) => e.agentId === agentId && (e.type === 'agent_start' || e.type === 'agent_complete')
      )
      if (idx === -1) return {}
      const realIdx = s.activityLog.length - 1 - idx
      const prefix = kind === 'input' ? '\n--- Input ---\n' : kind === 'output' ? '\n--- Output ---\n' : '\n--- Files ---\n'
      const updatedLog = s.activityLog.map((e, i) =>
        i === realIdx
          ? { ...e, detail: (e.detail || '') + prefix + content }
          : e
      )
      return { activityLog: updatedLog }
    }),

  setFiles: (files) => set((s) => {
    // Sanitize: ensure all file values are strings (backend may send objects)
    // Normalize paths: strip leading "./" and "/" to prevent duplicate tree entries
    const sanitized: Record<string, string> = {}
    for (const [k, v] of Object.entries(files)) {
      let normalized = k
      while (normalized.startsWith('./')) normalized = normalized.slice(2)
      while (normalized.startsWith('/')) normalized = normalized.slice(1)
      // Skip empty paths or bare directory markers
      if (!normalized) continue
      sanitized[normalized] = typeof v === 'string' ? v : JSON.stringify(v, null, 2)
    }
    return { generatedFiles: { ...s.generatedFiles, ...sanitized } }
  }),

  clearFiles: () => set({ generatedFiles: {} }),

  addPreviewLog: (line, service) =>
    set((s) => ({
      previewLogs: [...s.previewLogs.slice(-500), { service: service || '', line }],
    })),

  clearPreviewLogs: () => set({ previewLogs: [] }),

  triggerPreviewRefresh: () =>
    set((s) => ({ previewRefreshKey: s.previewRefreshKey + 1 })),

  setRoutingIntent: (intent) => set({ routingIntent: intent }),

  setRoutingInProgress: (v) => set({ routingInProgress: v }),

  // Phase F (D35): Error transparency actions
  addError: (error) =>
    set((s) => {
      const entry: BuildErrorEntry = { ...error, timestamp: Date.now() }
      return {
        errorsFound: s.errorsFound + 1,
        errorsFixed: error.fixed ? s.errorsFixed + 1 : s.errorsFixed,
        errorLog: [...s.errorLog, entry].slice(-200),
      }
    }),

  markErrorFixed: (index) =>
    set((s) => {
      if (index < 0 || index >= s.errorLog.length) return {}
      const updated = s.errorLog.map((e, i) =>
        i === index && !e.fixed ? { ...e, fixed: true } : e
      )
      const wasFixed = !s.errorLog[index].fixed
      return {
        errorLog: updated,
        errorsFixed: wasFixed ? s.errorsFixed + 1 : s.errorsFixed,
      }
    }),

  resetErrors: () =>
    set({ errorsFound: 0, errorsFixed: 0, errorLog: [] }),

  // Phase F (D28): Cost counter and ETA actions
  updateCost: (cost) => set({ currentCost: cost }),
  updateEta: (eta) => set({ estimatedEta: eta }),

  // E4 (D70): Quality and verification actions
  setQualityScore: (score, grade) => set({ qualityScore: score, qualityGrade: grade ?? null }),
  setVerificationInProgress: (v) => set({ verificationInProgress: v }),

  // Claim flow state
  claimErrorReason: null,
  claimRetryCount: 0,
  claimInProgress: false,
  claimJustCompleted: false,
  pendingAuthAction: null,
  marketingAssets: null,
  currentStage: null,
  autoRepair: null,
  setClaimError: (reason) => set({ claimErrorReason: reason }),
  clearClaimError: () => set({ claimErrorReason: null, claimRetryCount: 0 }),
  incrementClaimRetry: () => set((s) => ({ claimRetryCount: s.claimRetryCount + 1 })),
  setClaimInProgress: (v) => set({ claimInProgress: v }),
  setClaimJustCompleted: (v) => set({ claimJustCompleted: v }),
  setPendingAuthAction: (action) => set({ pendingAuthAction: action }),
  setMarketingAssets: (assets) => set({ marketingAssets: assets }),
  setAutoRepair: (state) => set({ autoRepair: state }),
  setCurrentStage: (stage) => set({ currentStage: stage }),

  reset: () => {
    set({
      buildPhase: 'idle',
      messages: [],
      activeAgents: {},
      taskGraph: null,
      completedTaskIds: new Set(),
      generatedFiles: {},
      isProcessing: false,
      currentBuildId: null,
      buildContextId: null,
      currentBuildNumber: 0,
      logs: [],
      activityLog: [],
      previewUrl: null,
      previewSandboxUrl: null,
      projectId: null,
      applicationId: null,
      previewLogs: [],
      previewRefreshKey: 0,
      buildFailedMessage: null,
      hasCompletedBuild: false,
      buildDiff: null,
      routingIntent: null,
      routingInProgress: false,
      previewStarting: false,
      previewRuntime: null,
      previewNoHostsAvailable: false,
      previewFailedMessage: null,
      deployStarted: false,
      deployCompleted: false,
      deployFailedMessage: null,
      deploymentUrl: null,
      errorsFound: 0,
      errorsFixed: 0,
      errorLog: [],
      currentCost: 0,
      estimatedEta: 0,
      qualityScore: null,
      qualityGrade: null,
      verificationInProgress: false,
      fileCreatedEvents: [],
      securityFindings: [],
      securitySummary: null,
      browserValidation: null,
      autoCreating: false,
      detectedDomain: null,
      postBuildSuggestions: [],
      currentStack: null,
      selectedDevice: 'desktop',
      orientation: 'portrait' as const,
      selectionMode: false,
      selectedComponent: null,
      componentTree: [],
      hoveredComponent: null,
      dragState: { dragging: false, sourceId: null, targetId: null, position: null },
      pendingStyleChanges: {},
      styleUndoStack: [],
      styleRedoStack: [],
      pendingReview: null,
      visualChatOpen: false,
      visualChatMessages: [],
      figmaImport: null,
      versions: [],
      selectedVersionId: null,
      collaborators: [],
      claimErrorReason: null,
      claimRetryCount: 0,
      claimInProgress: false,
      claimJustCompleted: false,
      pendingAuthAction: null,
      marketingAssets: null,
      currentStage: null,
      autoRepair: null,
    })
  },
}))

// TEMP DEBUG 2026-05-18 — expose the SAME store the page uses so Chrome
// console can read its state. Dynamic `import('/src/stores/buildStore.ts')`
// from devtools returns a separate Zustand instance, which made debugging
// the WS->store->ActivityFeed wiring impossible to verify.
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__qy_buildStore = useBuildStore
}
